import { alpha, createTheme, lighten, darken } from '@mui/material';
import '@mui/lab/themeAugmentation';

// Grafana-inspired dark theme colors
const themeColors = {
  primary: '#5794F2', // Grafana blue
  secondary: '#8F9BB3', // Grafana secondary text
  success: '#73BF69', // Grafana green
  warning: '#F2C96D', // Grafana yellow
  error: '#F2495C', // Grafana red
  info: '#5794F2', // Grafana blue
  black: '#1F1F23', // Grafana dark background
  white: '#E6E6E6', // Grafana light text
  primaryAlt: '#1F1F23' // Grafana dark background
};

const colors = {
  gradients: {
    blue1: 'linear-gradient(135deg, #5794F2 0%, #1F1F23 100%)',
    blue2: 'linear-gradient(135deg, #5794F2 0%, #1F1F23 100%)',
    blue3: 'linear-gradient(127.55deg, #1F1F23 3.73%, #2C3247 92.26%)',
    blue4: 'linear-gradient(-20deg, #1F1F23 0%, #2C3247 100%)',
    blue5: 'linear-gradient(135deg, #5794F2 10%, #1F1F23 100%)',
    orange1: 'linear-gradient(135deg, #F2C96D 0%, #F2495C 100%)',
    orange2: 'linear-gradient(135deg, #F2C96D 0%, #F2495C 100%)',
    orange3: 'linear-gradient(120deg, #F2C96D 0%, #F2495C 100%)',
    purple1: 'linear-gradient(135deg, #5794F2 0%, #1F1F23 100%)',
    purple3: 'linear-gradient(135deg, #5794F2 0%, #1F1F23 100%)',
    pink1: 'linear-gradient(135deg, #F2495C 0%, #1F1F23 100%)',
    pink2: 'linear-gradient(135deg, #F2495C 0%, #1F1F23 100%)',
    green1: 'linear-gradient(135deg, #73BF69 0%, #1F1F23 100%)',
    green2: 'linear-gradient(to bottom, #73BF69, #1F1F23)',
    black1: 'linear-gradient(100.66deg, #1F1F23 6.56%, #000000 93.57%)',
    black2: 'linear-gradient(60deg, #1F1F23 0%, #2C3247 100%)'
  },
  shadows: {
    success:
      '0px 1px 4px rgba(115, 191, 105, 0.25), 0px 3px 12px 2px rgba(115, 191, 105, 0.35)',
    error:
      '0px 1px 4px rgba(242, 73, 92, 0.25), 0px 3px 12px 2px rgba(242, 73, 92, 0.35)',
    info: '0px 1px 4px rgba(87, 148, 242, 0.25), 0px 3px 12px 2px rgba(87, 148, 242, 0.35)',
    primary:
      '0px 1px 4px rgba(87, 148, 242, 0.25), 0px 3px 12px 2px rgba(87, 148, 242, 0.35)',
    warning:
      '0px 1px 4px rgba(242, 201, 109, 0.25), 0px 3px 12px 2px rgba(242, 201, 109, 0.35)',
    card: '0px 9px 16px rgba(0, 0, 0, 0.3), 0px 2px 2px rgba(0, 0, 0, 0.4)',
    cardSm:
      '0px 2px 3px rgba(0, 0, 0, 0.3), 0px 1px 1px rgba(0, 0, 0, 0.4)',
    cardLg:
      '0 5rem 14rem 0 rgb(0 0 0 / 30%), 0 0.8rem 2.3rem rgb(0 0 0 / 60%), 0 0.2rem 0.3rem rgb(0 0 0 / 45%)'
  },
  layout: {
    general: {
      bodyBg: '#1F1F23' // Grafana dark background
    },
    sidebar: {
      background: '#1F1F23', // Grafana dark background
      textColor: themeColors.white,
      dividerBg: '#2C3247', // Grafana dark divider
      menuItemColor: themeColors.white,
      menuItemColorActive: themeColors.primary,
      menuItemBg: '#1F1F23', // Grafana dark background
      menuItemBgActive: '#2C3247', // Grafana dark active
      menuItemIconColor: themeColors.white,
      menuItemIconColorActive: themeColors.primary,
      menuItemHeadingColor: themeColors.white
    }
  },
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    black: {
      5: alpha(themeColors.black, 0.02),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black
    }
  },
  secondary: {
    lighter: lighten(themeColors.secondary, 0.85),
    light: lighten(themeColors.secondary, 0.25),
    main: themeColors.secondary,
    dark: darken(themeColors.secondary, 0.2)
  },
  primary: {
    lighter: lighten(themeColors.primary, 0.85),
    light: lighten(themeColors.primary, 0.3),
    main: themeColors.primary,
    dark: darken(themeColors.primary, 0.2)
  },
  success: {
    lighter: lighten(themeColors.success, 0.85),
    light: lighten(themeColors.success, 0.3),
    main: themeColors.success,
    dark: darken(themeColors.success, 0.2)
  },
  warning: {
    lighter: lighten(themeColors.warning, 0.85),
    light: lighten(themeColors.warning, 0.3),
    main: themeColors.warning,
    dark: darken(themeColors.warning, 0.2)
  },
  error: {
    lighter: lighten(themeColors.error, 0.85),
    light: lighten(themeColors.error, 0.3),
    main: themeColors.error,
    dark: darken(themeColors.error, 0.2)
  },
  info: {
    lighter: lighten(themeColors.info, 0.85),
    light: lighten(themeColors.info, 0.3),
    main: themeColors.info,
    dark: darken(themeColors.info, 0.2)
  }
};

export const PureDarkTheme = createTheme({
  colors: {
    gradients: colors.gradients,
    shadows: colors.shadows,
    alpha: colors.alpha,
    secondary: colors.secondary,
    primary: colors.primary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info
  },
  general: {
    reactFrameworkColor: '#5794F2', // Grafana blue
    borderRadiusSm: '6px',
    borderRadius: '10px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px'
  },
  sidebar: {
    background: colors.layout.sidebar.background,
    textColor: colors.layout.sidebar.textColor,
    dividerBg: colors.layout.sidebar.dividerBg,
    menuItemColor: colors.layout.sidebar.menuItemColor,
    menuItemColorActive: colors.layout.sidebar.menuItemColorActive,
    menuItemBg: colors.layout.sidebar.menuItemBg,
    menuItemBgActive: colors.layout.sidebar.menuItemBgActive,
    menuItemIconColor: colors.layout.sidebar.menuItemIconColor,
    menuItemIconColorActive: colors.layout.sidebar.menuItemIconColorActive,
    menuItemHeadingColor: colors.layout.sidebar.menuItemHeadingColor,
    boxShadow:
      '2px 0 3px rgba(0, 0, 0, 0.3), 1px 0 1px rgba(0, 0, 0, 0.4)',
    width: '290px'
  },
  header: {
    height: '80px',
    background: '#1F1F23', // Grafana dark background
    boxShadow: colors.shadows.cardSm,
    textColor: themeColors.white
  },
  spacing: 9,
  palette: {
    common: {
      black: colors.alpha.black[100],
      white: colors.alpha.white[100]
    },
    mode: 'dark',
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: colors.secondary.dark
    },
    error: {
      light: colors.error.light,
      main: colors.error.main,
      dark: colors.error.dark,
      contrastText: colors.alpha.white[100]
    },
    success: {
      light: colors.success.light,
      main: colors.success.main,
      dark: colors.success.dark,
      contrastText: colors.alpha.white[100]
    },
    info: {
      light: colors.info.light,
      main: colors.info.main,
      dark: colors.info.dark,
      contrastText: colors.alpha.white[100]
    },
    warning: {
      light: colors.warning.light,
      main: colors.warning.main,
      dark: colors.warning.dark,
      contrastText: colors.alpha.white[100]
    },
    text: {
      primary: colors.alpha.white[100],
      secondary: colors.alpha.white[70],
      disabled: colors.alpha.white[50]
    },
    background: {
      paper: '#1F1F23', // Grafana dark background
      default: colors.layout.general.bodyBg
    },
    action: {
      active: colors.alpha.white[100],
      hover: colors.primary.lighter,
      hoverOpacity: 0.1,
      selected: colors.alpha.white[10],
      selectedOpacity: 0.1,
      disabled: colors.alpha.white[50],
      disabledBackground: colors.alpha.white[5],
      disabledOpacity: 0.38,
      focus: colors.alpha.white[10],
      focusOpacity: 0.05,
      activatedOpacity: 0.12
    },
    tonalOffset: 0.5
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1840
    }
  },
  components: {
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(darken(themeColors.primaryAlt, 0.4), 0.2),
          backdropFilter: 'blur(2px)',

          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'blur(2px)'
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          marginLeft: 8,
          marginRight: 8,
          fontWeight: 'bold'
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': {
          width: '100%',
          height: '100%'
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          flex: 1
        },
        '#root': {
          width: '100%',
          height: '100%',
          display: 'flex',
          flex: 1,
          flexDirection: 'column'
        },
        html: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased'
        },
        '.child-popover .MuiPaper-root .MuiList-root': {
          flexDirection: 'column'
        },
        '#nprogress': {
          pointerEvents: 'none'
        },
        '#nprogress .bar': {
          background: colors.primary.lighter
        },
        '#nprogress .spinner-icon': {
          borderTopColor: colors.primary.lighter,
          borderLeftColor: colors.primary.lighter
        },
        '#nprogress .peg': {
          boxShadow:
            '0 0 15px ' +
            colors.primary.lighter +
            ', 0 0 8px' +
            colors.primary.light
        },
        ':root': {
          '--swiper-theme-color': colors.primary.main
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'div',
          h4: 'div',
          h5: 'div',
          h6: 'div',
          subtitle1: 'div',
          subtitle2: 'div',
          body1: 'div',
          body2: 'div'
        }
      },
      styleOverrides: {
        gutterBottom: {
          marginBottom: 4
        },
        paragraph: {
          fontSize: 17,
          lineHeight: 1.7
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: 'secondary.main',
            borderColor: 'secondary.main',
            boxShadow: 'none',
            height: 3,
            borderRadius: 0
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 48,
            height: 48,
            padding: '0 24px',
            fontSize: '1rem',
            color: colors.alpha.white[70],
            '&.Mui-selected': {
              color: themeColors.white,
              fontWeight: 600
            }
          }
        }
      }
    }
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 700,
      fontSize: 35,
      textTransform: 'none'
    },
    h2: {
      fontWeight: 700,
      fontSize: 30,
      textTransform: 'none'
    },
    h3: {
      fontWeight: 700,
      fontSize: 25,
      lineHeight: 1.4,
      color: colors.alpha.white[100],
      textTransform: 'none'
    },
    h4: {
      fontWeight: 700,
      fontSize: 16,
      textTransform: 'none'
    },
    h5: {
      fontWeight: 700,
      fontSize: 14,
      textTransform: 'none'
    },
    h6: {
      fontSize: 15,
      textTransform: 'none'
    },
    body1: {
      fontSize: 14,
      textTransform: 'none'
    },
    body2: {
      fontSize: 14,
      textTransform: 'none'
    },
    button: {
      fontWeight: 600,
      textTransform: 'none'
    },
    caption: {
      fontSize: 13,
      textTransform: 'none',
      color: colors.alpha.white[50]
    },
    subtitle1: {
      fontSize: 14,
      color: colors.alpha.white[70],
      textTransform: 'none'
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: 15,
      color: colors.alpha.white[70],
      textTransform: 'none'
    },
    overline: {
      fontSize: 13,
      fontWeight: 700,
      textTransform: 'none'
    }
  },
  shadows: [
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none'
  ]
}); 